import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { api, setTokenGetter } from '@/lib/api';
import { readOfflineScopes } from '@/features/events/offline/locators';
import { AuthContext } from './AuthContext';
import { lockEventVaults } from '@/features/events/offline/vault';
import { isPublicQuoteRoute, isOfflineEventRoute } from '@/lib/publicRoutes';
import { markPortalViewer } from '@/lib/portalViewerMarker';
import { markSessionAuthenticated, markSessionEnded, rememberSignInFailed } from '@/lib/sessionExpiry';
import type { UserProfile } from '@/types/user';
import {
  clearRedirectAttempt,
  markRedirectAttempt,
  navigateTo,
  oauthStartUrl,
  recentlyRedirected,
  shouldRedirectToSignIn,
} from './cognitoBoot';

type Provider = 'auth0' | 'cognito';
const PROVIDER: Provider = (import.meta.env.VITE_AUTH_PROVIDER === 'cognito' ? 'cognito' : 'auth0');

// The offline workspace only renders on a device with a prepared scope; without one the route guard
// sends the tab into the signed-in app, which needs the session bootstrapped.
function skipsSessionBootstrap(pathname: string): boolean {
  return isPublicQuoteRoute(pathname) || (isOfflineEventRoute(pathname) && readOfflineScopes().length > 0);
}

function Auth0ContextProvider({ children }: { children: ReactNode }) {
  const {
    isAuthenticated: auth0Authenticated,
    isLoading: auth0Loading,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
    user: auth0User,
  } = useAuth0();

  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setTokenGetter(getAccessTokenSilently);
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!navigator.onLine && window.location.pathname === '/' && readOfflineScopes().length > 0) {
      window.location.replace('/events/offline');
      return;
    }
    if (skipsSessionBootstrap(window.location.pathname)) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    if (auth0Loading) return;

    if (!auth0Authenticated) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchUser() {
      try {
        if (window.location.pathname === '/callback') {
          await api.post('/auth/callback', {
            name: auth0User?.name,
            picture: auth0User?.picture,
          });
        }
        const { data } = await api.get<UserProfile>('/auth/me');
        markPortalViewer();
        if (!cancelled) {
          setUser(data);
          markSessionAuthenticated();
        }
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchUser();
    return () => { cancelled = true; };
  }, [auth0Authenticated, auth0Loading, auth0User]);

  const login = useCallback(() => {
    loginWithRedirect({
      authorizationParams: {
        connection: 'google-oauth2',
      },
    });
  }, [loginWithRedirect]);

  const logout = useCallback(() => {
    lockEventVaults();
    setUser(null);
    markSessionEnded();
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  }, [auth0Logout]);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get<UserProfile>('/auth/me');
      setUser(data);
      markSessionAuthenticated();
    } catch {
      // Silently fail — user state unchanged
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: auth0Authenticated && user !== null,
        isLoading: auth0Loading || isLoading,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function CognitoContextProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(
    () => !skipsSessionBootstrap(window.location.pathname),
  );

  useEffect(() => {
    if (!navigator.onLine && window.location.pathname === '/' && readOfflineScopes().length > 0) {
      window.location.replace('/events/offline');
      return;
    }
    if (skipsSessionBootstrap(window.location.pathname)) return;

    // A tab that already holds a valid oauth2-proxy cookie signs in with this one call, skipping the
    // Cognito round trip. Without a session the edge answers with a CORS-readable 401 (oauth-errors
    // -> /auth/session-expired), and only then do we start the full-page sign-in.
    const apiBase = import.meta.env.VITE_API_BASE_URL ?? '';
    let cancelled = false;

    async function bootstrap() {
      try {
        const { data } = await api.post<UserProfile>('/auth/callback', {});
        markPortalViewer();
        if (cancelled) return;
        clearRedirectAttempt();
        setUser(data);
        markSessionAuthenticated();
        setIsLoading(false);
      } catch (err) {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } }).response?.status;
        if (shouldRedirectToSignIn(status, apiBase) && markRedirectAttempt()) {
          navigateTo(oauthStartUrl(apiBase, window.location.href));
          return;
        }
        // Coming straight back from sign-in and still failing would loop through Cognito forever.
        if (recentlyRedirected()) {
          clearRedirectAttempt();
          rememberSignInFailed();
        }
        setUser(null);
        setIsLoading(false);
      }
    }

    bootstrap();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(() => {
    const apiBase = import.meta.env.VITE_API_BASE_URL ?? '';
    if (!apiBase) {
      navigateTo('/');
      return;
    }
    // An explicit attempt counts toward the loop guard: a 401 on return shows the failure instead
    // of bouncing through OAuth a second time.
    markRedirectAttempt();
    navigateTo(oauthStartUrl(apiBase, window.location.href));
  }, []);

  const logout = useCallback(() => {
    lockEventVaults();
    setUser(null);
    markSessionEnded();
    clearRedirectAttempt();
    const apiBase = import.meta.env.VITE_API_BASE_URL ?? '';
    navigateTo(apiBase ? `${apiBase}/oauth2/sign_out` : '/oauth2/sign_out');
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get<UserProfile>('/auth/me');
      setUser(data);
      markSessionAuthenticated();
    } catch {
      // Silently fail
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: user !== null,
        isLoading,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (PROVIDER === 'cognito') {
    return <CognitoContextProvider>{children}</CognitoContextProvider>;
  }

  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;
  const redirectUri = import.meta.env.VITE_AUTH0_REDIRECT_URI || `${window.location.origin}/callback`;

  if (!domain || !clientId) {
    throw new Error('VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID must be set');
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      cacheLocation="localstorage"
      useRefreshTokens
      authorizationParams={{
        redirect_uri: redirectUri,
        audience,
        scope: 'openid profile email offline_access',
      }}
    >
      <Auth0ContextProvider>{children}</Auth0ContextProvider>
    </Auth0Provider>
  );
}
